import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import { StyledClickable } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CreditUseIndicator from "@store/Components/Store/AccountCenter/CreditUseIndicator";
import MessageLink from "@store/Components/Store/AccountCenter/MessagesLink";
import useCanSeeAccountCenter from "@store/Hooks/AccountCenter/useCanSeeAccountCenter";
import useIsSignedIn from "@store/Hooks/useIsSignedIn";
import userIsAuthenticated from "@store/Hooks/userIsAuthenticated";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    messageLinkDestination = "messageLinkDestination",
    creditIndicatorDestination = "creditIndicatorDestination",
    visibleForMobileOnly = "visibleForMobileOnly",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.messageLinkDestination]: LinkFieldValue;
        [fields.creditIndicatorDestination]: LinkFieldValue;
        [fields.visibleForMobileOnly]: boolean;
    };
}

type Props = OwnProps;

export interface AccountCenterLinksStyles {
    wrapper?: InjectableCss;
    hiddenWrapper?: InjectableCss;
    mobileWrapper?: InjectableCss;
}

export const accountCenterLinksStyles: AccountCenterLinksStyles = {
    wrapper: {
        css: css`
            border-left: 1px solid rgba(255, 255, 255, 0.25);
            padding-left: 0.4em;

            ${StyledClickable} {
                padding: 0 0.4em;
            }
        `,
    },
    hiddenWrapper: {
        css: css`
            height: 100%;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: ["inline-flex", null, "none"], properties: ["display"] }],
                    mediaOptions.min,
                )}
        `,
    },
    mobileWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
};

const styles = accountCenterLinksStyles;
const AccountCenterLinks: FC<Props> = ({ fields }) => {
    const isMobileOnly = fields.visibleForMobileOnly;
    const messageUrl = useGetLink(fields.messageLinkDestination).url;
    const creditIndicatorUrl = useGetLink(fields.creditIndicatorDestination).url;
    const isSignedIn = useIsSignedIn();
    const isAuthenticated = userIsAuthenticated();
    const canSeeAccountCenterLinks = useCanSeeAccountCenter();

    if (isSignedIn && isAuthenticated && canSeeAccountCenterLinks) {
        if (isMobileOnly) {
            return (
                <Hidden above="sm" {...styles.hiddenWrapper}>
                    <StyledWrapper {...styles.mobileWrapper}>
                        {messageUrl ? <MessageLink href={messageUrl} /> : null}
                        <CreditUseIndicator href={creditIndicatorUrl} />
                    </StyledWrapper>
                </Hidden>
            );
        }
        return (
            <StyledWrapper {...styles.wrapper}>
                {messageUrl ? <MessageLink href={messageUrl} /> : null}
                <CreditUseIndicator href={creditIndicatorUrl} />
            </StyledWrapper>
        );
    }

    return null;
};

const widgetModule: WidgetModule = {
    component: AccountCenterLinks,
    definition: {
        displayName: "Account Center Links",
        icon: "ax-link-list",
        group: "Header",
        fieldDefinitions: [
            {
                name: fields.messageLinkDestination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.creditIndicatorDestination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.visibleForMobileOnly,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
